import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { SvcGetPrivate } from '../services/userService.js';

function HealthStoryPage() {
  const navigate = useNavigate();
  const [expandedCard, setExpandedCard] = useState(null);
  const [data, setData] = useState(null);
  const [vitals, setVitals] = useState(null);
  const [reminderSet, setReminderSet] = useState(false);
  const [isVideoOpen, setIsVideoOpen] = useState(false);
  const [videoUrl, setVideoUrl] = useState("");
  const [videoType, setVideoType] = useState(""); // "mp4" or "youtube"

  const userName = localStorage.getItem('firstName') || 'User';

  // Fetch health vitals data
  useEffect(() => {
    SvcGetPrivate("/report/health-vitals")
      .then(res => {
        console.log("Vitals API response:", res.data);
        setData(res.data);
      })
      .catch(err => {
        console.error("API Error:", err);
        if (err.response?.status === 401) {
          navigate("/login", { replace: true });
        }
      });
  }, [navigate]);

  // Fetch health vitals status
  useEffect(() => {
    SvcGetPrivate("/report/health-vitals-status")
      .then(res => {
        console.log("Vitals Status response:", res.data);
        setVitals(res.data);
      })
      .catch(err => {
        console.error("API Error:", err);
      });
  }, []);

  const toggleCard = (cardId) => {
    setExpandedCard(expandedCard === cardId ? null : cardId);
  };

  const handleDownloadReport = () => {
    if (data?.report_pdf) {
      toast.success('Downloading your full health report...');
      window.open(data.report_pdf, "_blank");
    } else {
      toast.error("No report available to download");
    }
  };

  const handleNotifications = () => {
    setReminderSet(!reminderSet);
    if (!reminderSet) {
      toast.success('Reminder set for today! ✓');
    } else {
      toast.info('Reminder removed');
    }
  };

  const handleCardClick = (title) => {
    toast.info(`${title} - Coming Soon!`);
  };

  // For YouTube videos (Know More / Similar Stories section)
  const playYouTubeVideo = (url) => {
    const id = url.split("v=")[1]?.split("&")[0];
    setVideoUrl(id);
    setVideoType("youtube");
    setIsVideoOpen(true);
  };

  // For MP4 videos
  const playMP4Video = (url) => {
    setVideoUrl(url);
    setVideoType("mp4");
    setIsVideoOpen(true);
  };

  // Handle escape key and body scroll
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isVideoOpen) setIsVideoOpen(false);
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isVideoOpen]);

  useEffect(() => {
    document.body.style.overflow = isVideoOpen ? 'hidden' : 'unset';
  }, [isVideoOpen]);

  // Helper function to get status color
  const getStatusColor = (value, type) => {
    if (!value) return '#6b7280';
    
    switch(type) {
      case 'heart_rate':
        const hr = parseInt(value);
        return (hr >= 60 && hr <= 100) ? '#22c55e' : '#ef4444';
      case 'spo2':
        const spo2 = parseInt(value);
        return spo2 >= 95 ? '#22c55e' : '#ef4444';
      case 'stress':
        const stress = value.toString().trim().toUpperCase();
        return stress === 'NO' ? '#22c55e' : '#ef4444';
      default:
        return '#22c55e';
    }
  };

  // Helper function to get status text
  const getStatusText = (value, type) => {
    if (!value) return 'N/A';
    
    switch(type) {
      case 'heart_rate':
        const hr = parseInt(value);
        return (hr >= 60 && hr <= 100) ? 'Normal' : 'Attention Needed';
      case 'spo2':
        const spo2 = parseInt(value);
        return spo2 >= 95 ? 'Healthy' : 'Low';
      case 'stress':
        const stress = value.toString().trim().toUpperCase();
        return stress === 'NO' ? 'Low' : 'High';
      default:
        return 'Normal';
    }
  };

  if (!data || !vitals) {
    return (
      <section className="story-section">
        <div className="story-container">
          <p style={{ textAlign: 'center', marginTop: '50px', fontSize: '18px' }}>Loading your health story...</p>
        </div>
      </section>
    );
  }

  return (
    <section className="story-section">
      <div className="story-container">
        <header className="story-header">
          <div className="story-avatar"></div>
          <h1 className="story-header-title">Your Health Story</h1>
          <button 
            className={`story-icon-btn ${reminderSet ? 'active' : ''}`} 
            onClick={handleNotifications}
            title={reminderSet ? 'Reminder Set' : 'Set Reminder'}
          >
            <span className="material-symbols-outlined">
              {reminderSet ? 'notifications_active' : 'notifications'}
            </span>
          </button>
        </header>

        <main className="story-main">
          {/* Hero */}
          <div className="story-hero">
            <h2 className="story-hero-title">
              {data.title || `Your body found its rhythm today, ${userName}.`}
            </h2>
            <p className="story-hero-text">
              {data.summary || 'A story of graceful balance between energy and calm, adapting with inner wisdom.'}
            </p>
            
            {/* Video Button */}
            {data.video_url && (
              <button className="story-video-button" onClick={() => playMP4Video(data.video_url)}>
                <span className="material-symbols-outlined">play_circle</span>
                <span>{data.video_title || 'Watch Story'}</span>
              </button>
            )}
          </div>

          {/* System Insights */}
          <section className="story-insights">
            <h3 className="story-section-title">System Insights</h3>
            <div className="story-cards-list">
              {/* Cardiovascular - Expandable */}
              <div className="story-card">
                <div className="story-card-header" onClick={() => toggleCard('cardio')}>
                  <div className="story-card-icon">
                    <span className="material-symbols-outlined">favorite</span>
                  </div>
                  <div className="story-card-content">
                    <p>Cardiovascular System</p>
                    <p>Flowing with steady, life-giving force.</p>
                  </div>
                  <span className="material-symbols-outlined story-card-arrow">
                    {expandedCard === 'cardio' ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}
                  </span>
                </div>
                
                {expandedCard === 'cardio' && (
                  <div className="story-expand-content">
                    {/* Blood Pressure */}
                    <div className="story-info-box">
                      <h4>Blood Pressure</h4>
                      <p className="current-value" style={{ color: vitals?.vitals?.blood_pressure ? '#22c55e' : '#6b7280', fontWeight: 'bold', marginBottom: '8px' }}>
                        Current: {vitals?.vitals?.blood_pressure || 'N/A'}
                      </p>
                      <p className="normal">Normal Range: 90/60 to 120/80 mmHg</p>
                      <p className="risk">Risk-Indicating Range: ≥130/80 mmHg (Hypertension Stage 1); ≥140/90 mmHg (Stage 2); ≤90/60 mmHg (Hypotension)</p>
                    </div>

                    {/* Heart Rate */}
                    <div className="story-info-box">
                      <h4>Heart Rate (Pulse)</h4>
                      <p className="current-value" style={{ color: getStatusColor(vitals?.vitals?.heart_rate, 'heart_rate'), fontWeight: 'bold', marginBottom: '8px' }}>
                        Current: {vitals?.vitals?.heart_rate ? `${vitals.vitals.heart_rate} bpm` : 'N/A'} - {getStatusText(vitals?.vitals?.heart_rate, 'heart_rate')}
                      </p>
                      <p className="normal">Normal Range: 60–100 beats/min (resting)</p>
                      <p className="risk">Risk-Indicating Range: &lt;60 beats/min (Bradycardia); &gt;100 beats/min (Tachycardia)</p>
                    </div>

                    {/* Respiratory Rate */}
                    <div className="story-info-box">
                      <h4>Respiratory Rate</h4>
                      <p className="current-value" style={{ color: vitals?.vitals?.respiration_rate ? '#22c55e' : '#6b7280', fontWeight: 'bold', marginBottom: '8px' }}>
                        Current: {vitals?.vitals?.respiration_rate ? `${vitals.vitals.respiration_rate} breaths/min` : 'N/A'}
                      </p>
                      <p className="normal">Normal Range: 12–18 breaths/min</p>
                      <p className="risk">Risk-Indicating Range: &gt;20 breaths/min (Tachypnea); &lt;12 breaths/min (Bradypnea)</p>
                    </div>

                    {/* Oxygen Saturation */}
                    <div className="story-info-box">
                      <h4>Oxygen Saturation (SpO2)</h4>
                      <p className="current-value" style={{ color: getStatusColor(vitals?.vitals?.spo2, 'spo2'), fontWeight: 'bold', marginBottom: '8px' }}>
                        Current: {vitals?.vitals?.spo2 ? `${vitals.vitals.spo2}%` : 'N/A'} - {getStatusText(vitals?.vitals?.spo2, 'spo2')}
                      </p>
                      <p className="normal">Normal Range: 95-100%</p>
                      <p className="risk">Risk-Indicating Range: &lt;95% (Low oxygen levels)</p>
                    </div>

                    {/* Stress Level */}
                    <div className="story-info-box">
                      <h4>Stress Level</h4>
                      <p className="current-value" style={{ color: getStatusColor(vitals?.vitals?.stress_level, 'stress'), fontWeight: 'bold', marginBottom: '8px' }}>
                        Current: {getStatusText(vitals?.vitals?.stress_level, 'stress')}
                      </p>
                      <p className="normal">Optimal: Low stress levels</p>
                      <p className="risk">Risk-Indicating: High stress can affect cardiovascular health</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Nervous System */}
              <div className="story-card" onClick={() => handleCardClick('Nervous System')}>
                <div className="story-card-header">
                  <div className="story-card-icon">
                    <span className="material-symbols-outlined">psychology</span>
                  </div>
                  <div className="story-card-content">
                    <p>Nervous System</p>
                    <p>A calm mind, like a still sacred lake.</p>
                  </div>
                  <span className="material-symbols-outlined story-card-arrow">chevron_right</span>
                </div>
              </div>

              {/* Energy System */}
              <div className="story-card" onClick={() => handleCardClick('Energy System')}>
                <div className="story-card-header">
                  <div className="story-card-icon">
                    <span className="material-symbols-outlined">local_fire_department</span>
                  </div>
                  <div className="story-card-content">
                    <p>Energy System</p>
                    <p>Your inner fire (Agni) is bright and balanced.</p>
                  </div>
                  <span className="material-symbols-outlined story-card-arrow">chevron_right</span>
                </div>
              </div>
            </div>
            <p className="story-disclaimer">
              Health Spark provides insights for wellness and is not a substitute for professional medical advice.
            </p>
          </section>

          {/* Download Button */}
          <button className="story-download-btn" onClick={handleDownloadReport}>
            <span className="material-symbols-outlined">download</span>
            <span>Download Full Report</span>
          </button>

          {/* Key Pattern Connections */}
          <section>
            <h3 className="story-section-title">Key Pattern Connections</h3>
            <div className="story-card story-pattern-card">
              <div className="story-pattern-line"></div>
              <div className="story-pattern-item">
                <div className="story-pattern-icon secondary">
                  <span className="material-symbols-outlined">directions_walk</span>
                </div>
                <span>Morning Walk</span>
              </div>
              <div className="story-pattern-item">
                <div className="story-pattern-icon">
                  <span className="material-symbols-outlined">nightlight</span>
                </div>
                <span>Improved Sleep</span>
              </div>
              <div className="story-pattern-item">
                <div className="story-pattern-icon secondary">
                  <span className="material-symbols-outlined">mood</span>
                </div>
                <span>Better Mood</span>
              </div>
            </div>
          </section>

          {/* Action Strategy */}
          <div className="story-strategy-card">
            <h3>Your Action Strategy</h3>
            <div className="story-strategy-section">
              <h4>{data.tips_title || "Today's Quick Wins"}</h4>
              <div className="exercise-text" style={{ whiteSpace: 'pre-line' }}>
                {data.tips || '• 10-min post-lunch walk for digestion.\n• Glass of warm water with lemon.'}
              </div>
            </div>
            <div className="story-strategy-section">
              <h4>This Week's Support</h4>
              <ul>
                <li>Try one calming breathing exercise (Pranayama).</li>
                <li>Aim for 7-8 hours of consistent sleep.</li>
              </ul>
            </div>
            <div className="story-strategy-section">
              <h4>Long-term Resilience</h4>
              <ul>
                <li>Explore a mindfulness practice like Yoga Nidra.</li>
                <li>Build a consistent pre-sleep routine.</li>
              </ul>
            </div>
          </div>

          {/* Deeper Learning */}
          <section>
            <h3 className="story-section-title">Deeper Learning</h3>
            <div className="story-learning-grid">
              <div className="story-learning-item" onClick={() => handleCardClick('Guided Meditation')}>
                <div className="story-learning-img">
                  <div className="story-learning-badge">
                    <span className="material-symbols-outlined">timer</span> 5 min
                  </div>
                </div>
                <h4>Guided Meditation</h4>
                <p>Why? Calms the nervous system.</p>
              </div>
              <div className="story-learning-item" onClick={() => handleCardClick('Turmeric Elixir')}>
                <div className="story-learning-img turmeric"></div>
                <h4>Turmeric Elixir</h4>
                <p>Why? Reduces inflammation.</p>
              </div>
            </div>
          </section>

          {/* Similar Stories - Using Know More data if available */}
          <section>
            <h3 className="story-section-title">Similar Stories</h3>
            <div className="story-cards-list">
              {data.know_more && data.know_more.length > 0 ? (
                data.know_more.map((item, index) => (
                  <div key={index} className="story-card" onClick={() => playYouTubeVideo(item.url)}>
                    <div className="story-card-header">
                      <div className="story-card-icon">
                        <span className="material-symbols-outlined">play_circle</span>
                      </div>
                      <div className="story-card-content">
                        <p>{item.title}</p>
                        <p>{item.description}</p>
                      </div>
                      <span className="material-symbols-outlined story-card-arrow">chevron_right</span>
                    </div>
                  </div>
                ))
              ) : (
                <>
                  <div className="story-card" onClick={() => handleCardClick('Evening Routines')}>
                    <div className="story-card-header">
                      <div className="story-card-icon">
                        <span className="material-symbols-outlined">bedtime</span>
                      </div>
                      <div className="story-card-content">
                        <p>Evening Routines</p>
                        <p>Crafting a peaceful night's rest.</p>
                      </div>
                      <span className="material-symbols-outlined story-card-arrow">chevron_right</span>
                    </div>
                  </div>
                  <div className="story-card" onClick={() => handleCardClick('Managing Stress')}>
                    <div className="story-card-header">
                      <div className="story-card-icon">
                        <span className="material-symbols-outlined">self_improvement</span>
                      </div>
                      <div className="story-card-content">
                        <p>Managing Stress</p>
                        <p>Techniques for calm and clarity.</p>
                      </div>
                      <span className="material-symbols-outlined story-card-arrow">chevron_right</span>
                    </div>
                  </div>
                  <div className="story-card" onClick={() => handleCardClick('Boosting Daily Energy')}>
                    <div className="story-card-header">
                      <div className="story-card-icon">
                        <span className="material-symbols-outlined">local_fire_department</span>
                      </div>
                      <div className="story-card-content">
                        <p>Boosting Daily Energy</p>
                        <p>Natural ways to vitalize your day.</p>
                      </div>
                      <span className="material-symbols-outlined story-card-arrow">chevron_right</span>
                    </div>
                  </div>
                </>
              )}
            </div>
          </section>
        </main>
      </div>

      {/* Video Modal - Handles both MP4 and YouTube */}
      {isVideoOpen && (
        <div className="video-modal-overlay" onClick={() => setIsVideoOpen(false)}>
          <div className="video-modal-content" onClick={e => e.stopPropagation()}>
            <button className="video-close-btn" onClick={() => setIsVideoOpen(false)} title="Close Video">✖</button>
            <div className="video-wrapper" style={{ position: 'relative', paddingBottom: '56.25%', height: 0 }}>
              {videoType === "mp4" ? (
                <video
                  src={videoUrl}
                  controls
                  autoPlay
                  style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
                  title="Video Player"
                >
                  Your browser does not support the video tag.
                </video>
              ) : (
                <iframe
                  src={`https://www.youtube.com/embed/${videoUrl}?autoplay=1&controls=1`}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  title="YouTube Video"
                  style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
                ></iframe>
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

export default HealthStoryPage;